import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import DescriptionCard from './DescriptionCard';
import { ArrowLeft, User, Briefcase, Plus, Send, X, Loader2 } from 'lucide-react';

const IdeaDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const [idea, setIdea] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDesc, setNewDesc] = useState({ description: '', profit_loss_description: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchIdea();
    }, [id]);

    const fetchIdea = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/ideas/${id}`);
            setIdea(response.data.idea);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch idea details');
            setLoading(false);
        }
    };

    const handleAddDescription = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/ideas/${id}/idea_descriptions`,
                { idea_description: newDesc }
            );
            setShowAddForm(false);
            setNewDesc({ description: '', profit_loss_description: '' });
            fetchIdea();
        } catch (err) {
            alert("Failed to add description");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddClick = () => {
        if (!user) {
            navigate('/login', { state: { from: location } });
        } else {
            setShowAddForm(!showAddForm);
        }
    };

    if (loading) return (
        <div className="min-h-screen pt-20 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen pt-20 flex items-center justify-center text-red-400">
            {error}
        </div>
    );

    if (!idea) return null;

    return (
        <div className="min-h-screen pt-24 pb-20 relative">
            {/* Background */}
            <div className="absolute inset-0 bg-background -z-10 fixed">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-primary/5 blur-[120px] rounded-full"></div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8 group pl-2">
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    {t('details.back_to_feed')}
                </Link>

                {/* Idea Header Card */}
                <div className="bg-surface/50 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10 mb-10 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <div className="w-32 h-32 bg-gradient-to-br from-primary to-secondary rounded-full blur-3xl"></div>
                    </div>

                    <h1 className="text-3xl md:text-5xl font-bold font-display text-white mb-6 leading-tight">
                        {idea.title}
                    </h1>

                    <div className="flex items-center gap-4 text-sm text-slate-400 border-t border-white/5 pt-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                                <User className="w-4 h-4 text-primary" />
                            </div>
                            <span className="text-slate-200 font-medium">{idea.user.name}</span>
                        </div>
                        <div className="w-1 h-1 bg-slate-700 rounded-full"></div>
                        <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            <span>{idea.user.profession}</span>
                        </div>
                    </div>
                </div>

                {/* Initial Description (First Contribution) */}
                <div className="mb-12">
                    <h3 className="text-lg font-bold text-white mb-4 px-2 border-l-4 border-primary">{t('details.core_concept')}</h3>
                    {idea.descriptions && idea.descriptions.length > 0 && (
                        <DescriptionCard
                            description={idea.descriptions[0]}
                            onRatingUpdate={fetchIdea}
                            ideaId={idea.id}
                        />
                    )}
                </div>

                {/* Contributions Section */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <h3 className="text-lg font-bold text-white border-l-4 border-secondary pl-2">{t('details.community_perspectives')}</h3>
                        <button
                            onClick={handleAddClick}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${showAddForm
                                ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                                : 'bg-white text-slate-900 hover:bg-slate-200'
                                }`}
                        >
                            {showAddForm ? <><X className="w-4 h-4" /> {t('details.cancel')}</> : <><Plus className="w-4 h-4" /> {t('details.add_perspective')}</>}
                        </button>
                    </div>

                    {/* Add Form */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showAddForm ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-surface/40 border border-white/10 rounded-2xl p-6 mb-8">
                            <h4 className="text-white font-bold mb-4">{t('details.contribute_title')}</h4>
                            <form onSubmit={handleAddDescription} className="space-y-4">
                                <div>
                                    <label className="text-xs font-medium text-slate-400 mb-1.5 block">{t('details.perspective_label')}</label>
                                    <textarea
                                        value={newDesc.description}
                                        onChange={(e) => setNewDesc({ ...newDesc, description: e.target.value })}
                                        required
                                        className="w-full bg-black/20 border border-white/10 rounded-xl p-4 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors min-h-[120px]"
                                        placeholder={t('details.perspective_placeholder')}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-400 mb-1.5 block">{t('details.pl_label')}</label>
                                    <textarea
                                        value={newDesc.profit_loss_description}
                                        onChange={(e) => setNewDesc({ ...newDesc, profit_loss_description: e.target.value })}
                                        required
                                        className="w-full bg-black/20 border border-white/10 rounded-xl p-4 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 transition-colors min-h-[100px]"
                                        placeholder={t('details.pl_placeholder')}
                                    />
                                </div>
                                <div className="flex justify-end pt-2">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="bg-primary text-white px-6 py-2.5 rounded-xl font-bold hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        {t('details.submit_contribution')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* List of other descriptions (skipping the first one as it's the core concept) */}
                    <div className="space-y-6">
                        {idea.descriptions && idea.descriptions.slice(1).length > 0 ? (
                            idea.descriptions.slice(1).map(desc => (
                                <DescriptionCard
                                    key={desc.id}
                                    description={desc}
                                    ideaId={idea.id}
                                    onRatingUpdate={fetchIdea}
                                />
                            ))
                        ) : (
                            <div className="text-center py-10 border border-white/5 rounded-2xl bg-white/5 border-dashed">
                                <p className="text-slate-500">{t('details.no_perspectives')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IdeaDetail;
